import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, Card, Stack, Typography, useTheme } from '@mui/material';
import GoogleFontIcon from 'src/icons/GoogleFontIcon';
import type { RenterSignatureDoc } from './signatureFilters';
import SignatureDocRow from './SignatureDocRow';

interface RequestedSignaturesSectionProps {
  docs: RenterSignatureDoc[];
}

const RequestedSignaturesSection: FC<RequestedSignaturesSectionProps> = ({
  docs,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const sortedDocs = useMemo(
    () =>
      [...docs].sort((a, b) => a.documentTitle.localeCompare(b.documentTitle)),
    [docs]
  );

  const handleSignAll = useCallback(() => {
    if (sortedDocs.length > 0) {
      navigate(`/renter/signatures/sign/${sortedDocs[0].fsrId}`);
    }
  }, [navigate, sortedDocs]);

  if (sortedDocs.length === 0) {
    return null;
  }

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
            Signature Requested
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<GoogleFontIcon name="ink_pen" sx={{ fontSize: 14 }} />}
            onClick={handleSignAll}
            sx={{
              backgroundColor: theme.palette.success.dark,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: theme.palette.success.darker },
            }}
          >
            <span>Sign All</span>
          </Button>
        </Stack>
      </Box>
      <Stack gap={1.5} sx={{ p: 2 }}>
        {sortedDocs.map((doc) => (
          <SignatureDocRow key={doc.fsrId} doc={doc} mode="sign" />
        ))}
      </Stack>
    </Card>
  );
};

export default RequestedSignaturesSection;
